export const CATERING_DRAFT_ORDER_STATUSES = [
  'pending',
  'accepted',
  'rejected',
  'finalizing',
  'finalized',
  'cancelled',
] as const;

export type CateringDraftOrderStatus = typeof CATERING_DRAFT_ORDER_STATUSES[number];

// Only 'pending' (awaiting review) and 'accepted' (restaurant approved) drafts can be edited.
// Once finalizing/finalized/rejected/cancelled, the draft is frozen.
export function isCateringDraftOrderEditable(status?: CateringDraftOrderStatus): boolean {
  return status === 'pending' || status === 'accepted';
}

// Cancelled/rejected drafts have is_draft=false but still carry draft_status,
// so we check both fields to cover all edge cases.
export function isCateringDraftOrder(order: { is_draft?: boolean; draft_status?: string }): boolean {
  return !!order.is_draft || !!order.draft_status;
}

export interface CateringDraftOrderItemModifier {
  menu_item_id: string;
  qty?: number;
  id?: string;
  name?: string;
}

// Shared fields across all draft item representations (line item, pending, detail).
interface CateringDraftOrderItemBase {
  menu_item_id: string;
  pretax_cents: number;
  tax_cents: number;
  mods: CateringDraftOrderItemModifier[];
  special_instructions: string;
}

// Payload shape sent to the API when creating/editing a draft.
export interface CateringDraftOrderLineItem extends CateringDraftOrderItemBase {
  qty: number;
}

// Client-side pending item awaiting submission (carries a display name for UI).
export interface PendingCateringDraftOrderItem extends CateringDraftOrderLineItem {
  displayName: string;
}

// Client-only wrapper that adds a stable ID for list keying and removal.
export type PendingCateringDraftOrderItemWithId = PendingCateringDraftOrderItem & { _pendingId: number };

// Server-returned item with identity and status.
export interface CateringDraftOrderDetailItem extends CateringDraftOrderItemBase {
  id: string;
  name: string;
  status: string;
}

export type CateringMethod = 'pickup' | 'delivery';

export const CATERING_METHODS: readonly CateringMethod[] = ['pickup', 'delivery'];

// Display labels for the catering method, keyed by the stored value. Used by
// any UI surface that needs to show pickup vs delivery to a human (edit page
// title, owner column, OrderStatusPage drafts section, etc.) so the labels
// stay consistent and translation-ready in one place.
export const CATERING_METHOD_LABELS: Record<CateringMethod, string> = {
  pickup: 'Pickup',
  delivery: 'Delivery',
};

// Returns the display label for a catering_method value, or null when it's
// absent (pre-BBT-2693 catering rows). Callers branch on the null to omit
// the label from titles / pills / suffixes.
export const getCateringMethodLabel = (method: CateringMethod | null | undefined): string | null =>
  method ? CATERING_METHOD_LABELS[method] ?? null : null;

// Maps a fulfillment_method string to the catering_method it forces, or null
// when the fulfillment_method doesn't constrain the choice (legacy "catering"
// or any non-catering method). Mirrors the backend
// FULFILLMENT_METHOD_TO_CATERING_METHOD mapping in
// bb-railbot-app/clientapp/website/models/constants/fulfillment_methods.py —
// keep both in sync. Use to derive whether the consumer needs to pick or
// whether it's auto-set. String input (not the FulfillmentMethod enum) keeps
// callers free of import cycles between models/CateringDraftOrder and
// constants/FulfillmentMethods.
export const getForcedCateringMethodForFulfillment = (
  fulfillment_method: string | null | undefined
): CateringMethod | null => {
  if (fulfillment_method === 'catering_pickup') return 'pickup';
  if (fulfillment_method === 'catering_delivery') return 'delivery';
  return null;
};

export interface CateringDraftOrderDetail {
  orderId: string;
  orderNumber: number;
  draft_status: CateringDraftOrderStatus;
  draft_edit_count: number;
  catering_method: CateringMethod | null;
  fulfillment_date: string | null;
  lock_date: string | null;
  location_code: string | null;
  items: CateringDraftOrderDetailItem[];
  draft_rejected_reason?: string;
  draft_rejected_at?: string | null;
  draft_card_last4?: string | null;
  draft_card_brand?: string | null;
  payment_failed_at?: string | null;
  draft_merchant_edited_at?: string | null;
  draft_merchant_edit_summary?: string | null;
}
